import axios from "axios";
import { CategoryMapping } from "../models/CategoryMapping";

/**
 * RAG-30 — Correspondances de catégories.
 *   - GET  admins/mappings/categories          -> liste paginée + compteurs par onglet
 *   - PUT  admins/mappings/categories/{id}      -> rapproche (fixe la catégorie cible)
 *   - POST admins/categories/{id}/merge         -> fusionne une IT_* vers une catégorie cible
 * Le jeton admin est injecté par l'intercepteur axios.
 */

export interface MappingCounts {
  unmapped: number;
  auto: number;
  manual: number;
  total: number;
}

export interface MappingsList {
  mappings: CategoryMapping[];
  counts: MappingCounts;
  currentPage: number;
  lastPage: number;
}

function messageForStatus(error: any): string {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.error ?? error?.response?.data?.message;
  switch (status) {
    case 403:
      return "Vous n'avez pas les droits pour modifier les correspondances.";
    case 422:
      return apiMessage || "Requête invalide.";
    default:
      return "Veuillez contacter un administrateur";
  }
}

function list(status = "all", source = "", page = 1): Promise<MappingsList> {
  const params = new URLSearchParams();
  params.set("status", status);
  params.set("page", String(page));
  if (source) {
    params.set("source", source);
  }

  return axios
    .get(`${process.env.REACT_APP_API_URL}admins/mappings/categories?${params.toString()}`)
    .then((response) => ({
      mappings: (response.data?.data?.data ?? []).map((m: any) => new CategoryMapping(m)),
      counts: response.data?.counts ?? { unmapped: 0, auto: 0, manual: 0, total: 0 },
      currentPage: response.data?.data?.current_page ?? 1,
      lastPage: response.data?.data?.last_page ?? 1,
    }));
}

/** Rapproche une correspondance (fixe la catégorie cible) ; null = remettre non rapprochée. */
function update(id: number, categoryId: string | null): Promise<void> {
  return new Promise((resolve, reject) => {
    axios
      .put(`${process.env.REACT_APP_API_URL}admins/mappings/categories/${id}`, { category_id: categoryId })
      .then(() => resolve())
      .catch((error) => reject({ status: error?.response?.status, message: messageForStatus(error) }));
  });
}

/** Fusionne une catégorie (IT_*) vers une cible ; résout avec le nombre de produits déplacés. */
function merge(categoryId: string, targetCategoryId: string): Promise<number> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_API_URL}admins/categories/${encodeURIComponent(categoryId)}/merge`, {
        target_category_id: targetCategoryId,
      })
      .then((response) => resolve(response.data?.data?.moved_products ?? 0))
      .catch((error) => reject({ status: error?.response?.status, message: messageForStatus(error) }));
  });
}

export default {
  list,
  update,
  merge,
};
